import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  TrendingUp,
  Users,
  Target,
  Bell,
  Zap,
  ChevronRight,
  ChevronDown,
  Circle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const CampaignMetrics = ({ recentCampaignsList }) => {
  const [selectedDeal, setSelectedDeal] = useState("all");
  const navigate = useNavigate();
  const [deals] = useState([
    { id: "all", name: "All Deals" },
    { id: "deal1", name: "Summer Sale 2023" },
    { id: "deal2", name: "Flash Deals" },
    { id: "deal3", name: "Xmas Promo" },
  ]);

  const dealData = {
    all: [80, 40, 60, 20, 75, 45, 65],
    deal1: [65, 75, 45, 85, 25, 50, 70],
    deal2: [45, 60, 30, 70, 40, 55, 80],
    deal3: [55, 35, 65, 40, 60, 30, 50],
  };

  const chartData = {
    labels: [
      "Sept 10",
      "Sept 11",
      "Sept 12",
      "Sept 13",
      "Sept 14",
      "Sept 15",
      "Sept 16",
    ],
    datasets: [
      {
        label: "CTR",
        data: dealData[selectedDeal],
        borderColor: "#FF6B6B",
        backgroundColor: "rgba(255, 107, 107, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
          font: {
            size: window.innerWidth < 768 ? 10 : 12,
          },
        },
        grid: {
          display: window.innerWidth >= 768,
        },
      },
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            size: window.innerWidth < 768 ? 10 : 12,
          },
        },
        grid: {
          display: window.innerWidth >= 768,
        },
      },
    },
    interaction: {
      mode: "nearest",
      axis: "x",
      intersect: false,
    },
  };

  const quickActions = [
    {
      icon: <Zap size={18} />,
      title: "Uploaded Deals",
      description: "Deals you have uploaded",
      color: "bg-indigo-500",
      onClick: () => navigate("/offers"),
    },
    {
      icon: <Bell size={18} />,
      title: "Create a Deal",
      description: "Create a new deal",
      color: "bg-amber-500",
      onClick: () => navigate("/offers/new"),
    },
  ];

  const StatusBadge = ({ value }) => {
    const statusConfig = {
      approved: {
        className: "bg-green-50 text-xs text-green-700 border border-green-200",
        icon: <Circle size={12} className="text-green-500 fill-current" />,
      },
      draft: {
        className: "bg-gray-50 text-xs text-gray-700 border border-gray-200",
        icon: <Circle size={12} className="text-gray-500" />,
      },
      pending: {
        className: "bg-yellow-50 text-xs text-yellow-700 border border-yellow-200",
        icon: <Circle size={12} className="text-yellow-500" />,
      },
      rejected: {
        className: "bg-red-50 text-xs text-red-700 border border-red-200",
        icon: <Circle size={12} className="text-red-500" />,
      },
    };
    let status = value.toLowerCase();
    const config = statusConfig[status];

    return (

        <span
          className={`flex items-center gap-1.5 px-3 py-1 text-[10px] font-medium rounded-full ${config.className}`}
        >
          {config.icon}
          <span className="text-[10px]">{value}</span>
        </span>
    );
  };

  return (
    <div className="relative w-full max-w-[100vw] py-4 px-4 md:px-8 space-y-6 overflow-x-hidden">
      <div className="grid grid-cols-12 gap-4 max-w-full">
        {/* Chart Section */}
        <div className="col-span-12 p-4 bg-white rounded-2xl shadow-sm lg:col-span-8 md:p-6">
          <div className="flex flex-col gap-4 justify-between mb-6 md:flex-row md:items-center">
            <h2 className="text-lg font-semibold text-gray-800 md:text-xl">
              Live Deals CTR
            </h2>
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 md:flex-none">
                <select
                  value={selectedDeal}
                  onChange={(e) => setSelectedDeal(e.target.value)}
                  className="w-full md:w-auto appearance-none pl-4 pr-10 py-2.5 text-sm bg-white rounded-xl border-2 border-pink-100 
                    text-gray-700 font-medium hover:border-pink-200 focus:border-pink-300 focus:ring-2 
                    focus:ring-pink-100 focus:outline-none transition-all cursor-pointer min-w-[180px]
                    shadow-sm hover:shadow-md"
                >
                  {deals.map((deal) => (
                    <option
                      key={deal.id}
                      value={deal.id}
                      className="py-2 text-gray-700"
                    >
                      {deal.name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown size={16} className="text-pink-400" />
                </div>
                <div className="absolute top-0 bottom-0 left-0 w-1 bg-gradient-to-b from-pink-400 to-pink-500 rounded-l-xl" />
              </div>

              <div className="relative flex-1 group md:flex-none">
                <button
                  className="w-full md:w-auto px-4 py-2.5 text-sm rounded-xl border-2 border-pink-100 text-gray-700 
                  font-medium hover:border-pink-200 focus:border-pink-300 focus:ring-2 focus:ring-pink-100 
                  focus:outline-none transition-all bg-white shadow-sm hover:shadow-md flex items-center gap-2 justify-center"
                >
                  <span className="w-2 h-2 bg-pink-400 rounded-full" />
                  Weekly
                </button>
                <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-pink-400 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
              </div>
            </div>
          </div>
          <div className="h-[250px] md:h-[400px]">
            <Line data={chartData} options={options} />
          </div>
        </div>

        <div className="col-span-12 space-y-6 lg:col-span-4">
          {/* Quick Actions */}
          <div className="bg-white rounded-2xl shadow-sm p-6 h-[250px]">
            <h3 className="mb-4 text-lg font-semibold text-gray-800">
              Quick Actions
            </h3>
            <div className="space-y-3">
              {quickActions.map((action, index) => (
                <motion.button
                  onClick={action.onClick}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  key={index}
                  className="flex justify-between items-center p-4 w-full bg-gray-50 rounded-xl transition-colors hover:bg-gray-100 group"
                >
                  <div className="flex gap-4 items-center">
                    <div
                      className={`p-2 rounded-lg ${action.color} text-white`}
                    >
                      {action.icon}
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium">{action.title}</p>
                      <p className="text-xs text-gray-600">
                        {action.description}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="text-gray-400 transition-colors group-hover:text-gray-600" />
                </motion.button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-6 h-[250px] overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Recent Deals</h3>
              <button onClick={() => navigate('/offers')} className="text-xs text-pink-500 font-medium flex items-center hover:text-pink-600 transition-colors">
                View all <ChevronRight size={14} />
              </button>
            </div>
            <div className="mt-4 space-y-1 overflow-y-auto max-h-[180px] pr-1">
              {recentCampaignsList && recentCampaignsList.length > 0 ? (
                recentCampaignsList.slice(0, 3).map((campaign) => (
                  <div
                    key={campaign.id}
                    className="flex justify-between items-center p-3 rounded-lg bg-gray-50 border border-gray-100 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center text-pink-500">
                        <Target size={16} />
                      </div>
                      <span className="pr-3 text-[12px] font-medium text-gray-700 truncate">
                        {campaign.title && campaign.title.length > 13 ? campaign.title.substring(0, 13) + "..." : campaign.title}
                      </span>
                    </div>
                    <StatusBadge value={campaign.status} />
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400 gap-2">
                  <Target size={24} />
                  <p className="text-sm">No active deals</p>
                  <button 
                    onClick={() => navigate('/offers/new')}
                    className="text-xs text-pink-500 font-medium hover:underline"
                  >
                    Create your first deal
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignMetrics;
